import { vapi } from '../vapi';

/**
 * Wraps the @vapi-ai/web singleton behind the common provider contract.
 * Message-type mapping mirrors the logic that used to live in VapiContext.
 */
export const createVapiProvider = () => {
  const listeners = new Map(); // event → Set<handler>
  const emit = (event, payload) => {
    (listeners.get(event) || []).forEach((h) => { try { h(payload); } catch (e) { console.error(`[vapiProvider] ${event} handler threw`, e); } });
  };

  const onMessage = (message) => {
    emit('raw-message', message);
    if (message.type === 'transcript') {
      if (message.transcriptType !== 'final' || !message.transcript) return;
      const role = message.role === 'assistant' || message.speaker === 'assistant' || message.user === false || message.isAssistant === true ? 'assistant' : 'user';
      emit('transcript', { role, text: message.transcript, mode: 'append' });
      return;
    }
    if (message.type === 'conversation-update' && Array.isArray(message.messages)) {
      const reversed = [...message.messages].reverse();
      const last = reversed[0];
      if (last && last.role === 'tool_call_result' && last.toolCallId && last.result) {
        const call = reversed.find((m) => m.role === 'tool_calls');
        let args = {};
        try { args = JSON.parse(call?.toolCalls?.[0]?.function?.arguments ?? '{}'); } catch { args = {}; }
        emit('tool-result', { name: last.name, result: last.result, args, raw: message });
      }
      return;
    }
    if (message.type === 'status-update' && message.status === 'ended') {
      emit('status-ended', { reason: message.endedReason ?? null });
    }
  };

  const bindings = [
    ['call-start', () => emit('call-start')],
    ['call-end', (e) => emit('call-end', { reason: e?.endedReason ?? null })],
    ['speech-start', () => emit('speech-start')],
    ['speech-end', () => emit('speech-end')],
    ['speaking-start', () => emit('speaking-start')],
    ['speaking-end', () => emit('speaking-end')],
    ['message', onMessage],
    ['error', (e) => emit('error', e)],
  ];
  if (vapi) bindings.forEach(([e, h]) => vapi.on(e, h));

  return {
    name: 'vapi',
    isAvailable: () => !!vapi,
    async start(config) {
      if (!vapi) throw new Error('Vapi not initialized. Please check your API key configuration.');
      const raw = await vapi.start(config);
      return { callId: raw?.id ?? null, raw };
    },
    async stop() { if (vapi) await vapi.stop(); },
    setMuted(muted) { if (vapi) vapi.setMuted(muted); },
    say(text) { if (vapi) vapi.say(text, false); },
    send(message) { if (vapi) vapi.send(message); },
    on(event, handler) { if (!listeners.has(event)) listeners.set(event, new Set()); listeners.get(event).add(handler); },
    off(event, handler) { listeners.get(event)?.delete(handler); },
    destroy() { if (vapi) bindings.forEach(([e, h]) => vapi.off(e, h)); listeners.clear(); },
  };
};
